import { type LoaderFunctionArgs, json } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { Flex } from '#app/components/layout/flex.tsx'
import { Separator } from '#app/components/layout/separator.tsx'
import { P } from '#app/components/typography/p.tsx'
import { sessionKey } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { authSessionStorage } from '#app/utils/session.server.ts'
import { readSource } from '../sections/sections.server.tsx'
import { Description, Features, Source, type componentProps, Parts, Usage } from '../sections/sections.tsx'

export const handle = {
	breadcrumb: 'Separator',
}

const component: componentProps = {
	name: 'Separator',
	shortName: 'Separator',
	fileName: 'separator',
	category: 'layout',
	description: ['Visually or semantically separates content.'],
	features: ['Supports horizontal and vertical orientations.', 'Support visibility in the accessibility tree.'],
	parts: [
		{
			name: 'Separator',
			description: 'Visually or semantically separates content.',
			props: [
				{ name: 'orientation', type: "'horizontal' | 'vertical'", default: "'horizontal'", description: 'The orientation of the separator.' },
				{ name: 'decorative', type: 'boolean', default: 'false', description: 'Whether the separator is decorative or not.' },
			],
		},
	],
	usage: `<div className="max-w-96">
	<div className="space-y-1">
		<P size="md" weight="bold">
			VerveUI
		</P>
		<P size="sm">
			A UI component library.
		</P>
	</div>
	<Separator className="my-4" />
	<Flex gap="10" align="center" className="h-5">
		<P size="sm">
			Blog
		</P>
		<Separator orientation="vertical" />
		<P size="sm">
			Docs
		</P>
		<Separator orientation="vertical" />
		<P size="sm">
			Source
		</P>
	</Flex>
</div>`,
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
	const authSession = await authSessionStorage.getSession(request.headers.get('cookie'))
	const sessionId = authSession.get(sessionKey)
	const session =
		sessionId ?
			await prisma.session.findUnique({
				select: { user: { select: { id: true, paid: true } } },
				where: { id: sessionId, expirationDate: { gt: new Date() } },
			})
		:	null
	const source = session?.user.paid ? await readSource({ category: component.category, fileName: component.fileName }) : null
	return json({ source })
}

const SeparatorRoute = () => {
	const { source } = useLoaderData<typeof loader>()
	const { name, description, shortName, fileName, features, usage, parts } = component

	return (
		<>
			<Description name={name} description={description} />
			<Features features={features} />
			<Parts parts={parts} />
			<Usage usage={usage} shortName={shortName} />
			<div className="my-10 max-w-md rounded-lg border p-6">
				<div className="space-y-1">
					<P className="text-bold">VerveUI</P>
					<P className="text-sm">A UI component library.</P>
				</div>
				<Separator className="my-4" />
				<Flex gap="10" items="center" className="h-5">
					<P className="text-sm">Blog</P>
					<Separator orientation="vertical" />
					<P className="text-sm">Docs</P>
					<Separator orientation="vertical" />
					<P className="text-sm">Source</P>
				</Flex>
			</div>
			<Source source={source} fileName={fileName} />
		</>
	)
}
export default SeparatorRoute
